import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Send, Loader2, AlertCircle, ImagePlus, X } from "lucide-react";
import { track } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls =
  "w-full rounded-xl border border-white/10 bg-ink/70 px-4 py-3.5 text-sm sm:text-base text-slate-100 placeholder:text-slate-500 outline-none transition-colors duration-300 focus:border-accent/60";
const labelCls = "mb-2 block font-mono text-[10px] tracking-[0.24em] uppercase text-mute";

export const DmeForm = () => {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", vehicle: "", mods: "",
    goal: "Performance Tuning", message: "", company: "",
  });
  const [photos, setPhotos] = useState([]);
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("type", "dme");
      fd.append("carrier", "");
      fd.append("contactMethod", "Phone");
      photos.forEach((p) => fd.append("photos", p));
      const res = await fetch(`${API}/estimate`, { method: "POST", body: fd });
      if (!res.ok) throw new Error("send failed");
      track("form_submit", { form: "dme" });
      navigate("/thank-you");
    } catch {
      setStatus("error");
    }
  };

  return (
    <form data-testid="dme-form" onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      <div>
        <label htmlFor="dme-name" className={labelCls}>Full Name *</label>
        <input id="dme-name" data-testid="dme-name-input" required value={form.name} onChange={set("name")} className={inputCls} maxLength={120} />
      </div>
      <div>
        <label htmlFor="dme-phone" className={labelCls}>Phone *</label>
        <input id="dme-phone" data-testid="dme-phone-input" type="tel" required value={form.phone} onChange={set("phone")} className={inputCls} maxLength={40} />
      </div>
      <div>
        <label htmlFor="dme-email" className={labelCls}>Email *</label>
        <input id="dme-email" data-testid="dme-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} />
      </div>
      <div>
        <label htmlFor="dme-vehicle" className={labelCls}>Vehicle Year / Make / Model *</label>
        <input id="dme-vehicle" data-testid="dme-vehicle-input" required value={form.vehicle} onChange={set("vehicle")} placeholder="2021 BMW M4 Competition" className={inputCls} maxLength={120} />
      </div>
      <div>
        <label htmlFor="dme-goal" className={labelCls}>Your Goal *</label>
        <select id="dme-goal" data-testid="dme-goal-select" value={form.goal} onChange={set("goal")} className={inputCls}>
          <option>Performance Tuning</option>
          <option>DME / ECU Repair</option>
          <option>Collision Repair</option>
          <option>Not Sure — Advise Me</option>
        </select>
      </div>
      <div>
        <label htmlFor="dme-mods" className={labelCls}>Current Modifications (optional)</label>
        <input id="dme-mods" data-testid="dme-mods-input" value={form.mods} onChange={set("mods")} placeholder="Intake, exhaust, downpipes…" className={inputCls} maxLength={300} />
      </div>
      <div className="sm:col-span-2">
        <label htmlFor="dme-notes" className={labelCls}>Notes / Symptoms / Goals *</label>
        <textarea id="dme-notes" data-testid="dme-notes-input" required rows={4} value={form.message} onChange={set("message")} placeholder="Describe what you want from the tune, or the fault symptoms you're seeing…" className={`${inputCls} resize-none`} maxLength={4000} />
      </div>
      <div className="sm:col-span-2">
        <span className={labelCls}>Photos (up to 6 — vehicle, engine bay, fault codes)</span>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          data-testid="dme-photo-input"
          onChange={(e) => {
            setPhotos((p) => [...p, ...Array.from(e.target.files || [])].slice(0, 6));
            e.target.value = "";
          }}
        />
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" data-testid="dme-photo-button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 rounded-xl border border-dashed border-white/20 bg-ink/50 px-5 py-3.5 text-sm text-slate-300 transition-colors hover:border-accent/50 hover:text-accent">
            <ImagePlus size={16} />
            {photos.length ? "Add more photos" : "Upload photos"}
          </button>
          {photos.map((p, i) => (
            <span key={`${p.name}-${i}`} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-surface px-3 py-1.5 text-xs text-slate-400">
              {p.name.length > 22 ? `${p.name.slice(0, 19)}…` : p.name}
              <button type="button" aria-label={`Remove ${p.name}`} onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="text-slate-500 hover:text-red-400">
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      </div>
      <div className="hidden" aria-hidden="true">
        <label htmlFor="dme-company">Company</label>
        <input id="dme-company" tabIndex={-1} autoComplete="off" value={form.company} onChange={set("company")} />
      </div>
      <div className="sm:col-span-2 flex flex-col sm:flex-row items-start sm:items-center gap-5">
        <button type="submit" data-testid="dme-submit-button" disabled={status === "sending"} className="inline-flex items-center gap-3 rounded-full bg-accentdeep px-8 py-4 font-display font-bold text-sm sm:text-base text-white transition-all duration-300 hover:bg-accenthover hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0">
          {status === "sending" ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} strokeWidth={2.2} />}
          {status === "sending" ? "Sending…" : "Request a Consultation"}
        </button>
        {status === "error" && (
          <p data-testid="dme-form-error" className="inline-flex items-center gap-2 text-sm text-red-400">
            <AlertCircle size={15} />
            Something went wrong — please call (214) 888-2886 instead.
          </p>
        )}
      </div>
    </form>
  );
};
